/**
 * Where you were, per screen.
 *
 * TWO COMPLAINTS, ONE CAUSE.
 *
 *  1. "You have to hit the button twice to start moving." A freshly arrived
 *     page often has focus on nothing, and `move()` deliberately treats the
 *     first press as "take the first element" rather than doing nothing. That
 *     is the right fallback and the wrong normal case: the press is spent
 *     claiming focus instead of moving.
 *
 *  2. "Back from a film returns to the top-left." You were four hundred films
 *     into a grid, looked at one, came back, and had to find your place again
 *     — every time, on a library of five thousand.
 *
 * Both are the same thing: nothing remembers where the cursor was. So this
 * does, keyed by screen, and puts it back on arrival. It works because the
 * `data-nav` id of a card is derived from the item (`lib-<ratingKey>`), so it
 * identifies the same film across an unmount — a remembered INDEX would point at
 * whatever now happens to be fourth.
 *
 * WHY IT POLLS RATHER THAN RESTORING ONCE.
 *
 * The element is usually not there yet. Coming back to a section means a
 * render, then a cache read, then a paint — and on a Fire TV Stick with four
 * hundred posters that is several frames. A single attempt on mount finds
 * nothing and gives up silently, which is indistinguishable from not having
 * built this at all. So it watches for the element to appear, briefly.
 *
 * It gives up the moment the person does anything, which is the part that
 * keeps it from being annoying: if they press a key while the page is still
 * settling, that press wins and nothing yanks the cursor afterwards.
 */

const remembered = new Map<string, string>();

/** How long to wait for the remembered element to render before giving up. */
const WINDOW_MS = 1500;

let attempt: { key: string; until: number; raf: number } | null = null;

/** The screen, including its query — /search?q=saw is not /search?q=batman. */
export function screenKey(pathname: string, search: string): string {
  return pathname + search;
}

/** Note that this element has the cursor, so we can come back to it. */
export function noteFocus(key: string, navId: string): void {
  remembered.set(key, navId);
  /*
   * Bounded, because this map otherwise grows one entry per distinct screen
   * for the life of the tab — and every search query is a distinct screen, so
   * somebody who searches a lot would accumulate hundreds. Oldest out first;
   * Map preserves insertion order, and re-noting deletes before setting so
   * "recently used" is what survives.
   */
  if (remembered.size > 50) {
    const oldest = remembered.keys().next().value;
    if (oldest !== undefined && oldest !== key) remembered.delete(oldest);
  }
}

export function forgetFocus(key: string): void {
  remembered.delete(key);
}

/**
 * Is a restore still looking for its element?
 *
 * Read by `<Focusable autoFocus>`, which must stand down while one is in
 * flight. Without this both run: autoFocus takes the first card, the restore
 * then finds the remembered one a few frames later and moves the cursor — a
 * correct destination reached via a visible jump, which at ten feet reads as
 * the screen twitching.
 */
export function restorePending(): boolean {
  return attempt !== null;
}

/** Stop trying. Called when the person takes over. */
export function cancelRestore(): void {
  if (!attempt) return;
  cancelAnimationFrame(attempt.raf);
  attempt = null;
}

/**
 * Put the cursor back where it was on this screen, once the element exists.
 *
 * Returns immediately; the work happens over the next few frames.
 */
export function restoreFocus(key: string, fallbackNavId?: string): void {
  cancelRestore();
  const navId = remembered.get(key) ?? fallbackNavId;
  if (!navId) return;

  const until = Date.now() + WINDOW_MS;
  const tick = () => {
    if (!attempt) return;

    const el = document.querySelector<HTMLElement>(`[data-nav="${CSS.escape(navId)}"]`);
    if (el) {
      attempt = null;
      el.focus();
      /*
       * CENTRED, not merely visible. `focus()` scrolls the minimum distance to
       * bring an element into view, which parks a restored card flush against
       * the top or bottom edge — technically visible, and it reads as "the
       * page jumped somewhere odd" rather than "here is where you were".
       */
      el.scrollIntoView({ block: 'center', inline: 'center' });
      return;
    }

    if (Date.now() > until) { attempt = null; return; }
    attempt.raf = requestAnimationFrame(tick);
  };

  attempt = { key, until, raf: requestAnimationFrame(tick) };
}
